import { useEffect, useState, FC } from 'react';
import { Skeleton, Typography } from '@mui/material';
import { Job, Rooftop } from '@libs/models';
import { useForm } from '@carcareers/contexts';
import { AutocompleteInput } from '@carcareers/components';
import { jobApi } from '@carcareers/store';
import { PostStepperDto } from '@libs/dto';

interface JobPostSelectProps {
  name: string;
  rooftopName?: string;
}

export const JobPostSelect: FC<JobPostSelectProps> = ({ name, rooftopName = 'rooftop' }) => {
  const { getData, setErrors, errors, data } = useForm<PostStepperDto>();
  const [jobs, setJobs] = useState<Job[]>([]);
  const rooftop = getData<Rooftop>(rooftopName);

  const rooftopId = rooftop?.id || 0;
  const { data: rooftopJobs, isLoading: isLoadingRooftopJobs } = jobApi.useJobFindByRooftopQuery(
    { rooftopId, page: 1, limit: 100 },
    { skip: !rooftop?.id },
  );

  useEffect(() => {
    if (rooftopId && !isLoadingRooftopJobs && rooftopJobs) {
      setJobs(rooftopJobs.items || []);
      // Clear errors when jobs are loaded for selected rooftop
      if (errors && errors.length > 0) {
        setErrors((prevErrors) => prevErrors.filter((error) => error.property !== name));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, rooftopJobs, isLoadingRooftopJobs, rooftopId]);

  if (rooftopId && !jobs?.length) {
    return (
      <Typography variant="subtitle2">
        The selected rooftop does not have any Job Templates associated with it. Please create a new Job Template.
      </Typography>
    );
  }

  if (!rooftopId) {
    return <Typography variant="subtitle2">You cannot select a job without an existing rooftop.</Typography>;
  }

  return !jobs.length ? (
    <Skeleton variant="rounded" height={56} />
  ) : (
    <AutocompleteInput
      name={name}
      label="Job"
      options={jobs.map((job) => ({ label: `${job.id} - ${job.title}`, value: job }))}
      value={data?.job?.id ? { label: data.job.title, value: data.job } : null}
    />
  );
};

export default JobPostSelect;
